import { css, html, LitElement, nothing } from "lit";
import { shared } from "../styles";
import { customElement, property, query } from "lit/decorators.js";
import {
    colors,
    DailyWeatherPrediction,
    formatDate,
    formatNumber,
    HourlyWeatherPrediction,
    toTimeString,
} from "@pentacode/core";
import "./popover";
import { Chart } from "./chart";
import { popover } from "../directives/popover";

const WEATHER_ICONS_PATH = "img/weather-icons/fill";

@customElement("ptc-weather-detailed")
export class WeatherDetailed extends LitElement {
    @property({ attribute: false })
    data: DailyWeatherPrediction;

    @property()
    city: string;

    @query("ptc-chart")
    private _chart: Chart;

    private async _updateChart() {
        await this.updateComplete;
        if (!this._chart) {
            return;
        }
        this._chart.config = this._getChartConfig();
    }

    private _getChartConfig() {
        const series = [
            {
                type: "line",
                name: "Temperatur",
                data:
                    this.data?.hourly.map((r) => ({
                        x: toTimeString(r.time),
                        y: r.temperature,
                    })) || [],
            },
            {
                type: "area",
                name: "Niederschlag.",
                data:
                    this.data?.hourly.map((r) => ({
                        x: toTimeString(r.time),
                        y: r.precipitation.amount,
                    })) || [],
            },
            {
                type: "line",
                name: "Niederschlagwarschl.",
                data:
                    this.data?.hourly.map((r) => ({
                        x: toTimeString(r.time),
                        y: r.precipitation.propability,
                    })) || [],
            },
        ];

        const categories = this.data?.hourly.map((r) => toTimeString(r.time)) || [];

        return {
            chart: {
                type: "line" as const,
                height: 200,
                width: 350,
                selection: { enabled: false },
                parentHideOffset: true,
                zoom: { enabled: false },
            },
            legend: {
                show: false,
            },
            dataLabels: {
                enabled: false,
            },
            series,
            xaxis: {
                type: "category" as const,
                categories,
                axisTicks: { show: false },
                labels: {
                    datetimeUTC: false,
                    formatter: function (hour: string) {
                        if (["03:00", "06:00", "09:00", "12:00", "15:00", "18:00", "21:00"].includes(hour)) {
                            return hour.slice(0, 2);
                        }
                        return "";
                    },
                    style: {
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#444",
                        fontFamily: "Nunito",
                    },
                },
            },
            colors: [colors.orange, colors.blue, colors.aqua],
            yaxis: [
                {
                    show: false,
                    opposite: true,
                    title: {
                        text: "Temperatur",
                        style: {
                            color: colors.orange,
                        },
                    },
                    labels: {
                        formatter: (val: number) => formatNumber(val, 1),
                        style: {
                            colors: colors.orange,
                        },
                    },
                    axisBorder: {
                        show: false,
                        color: colors.blue,
                    },
                    axisTicks: {
                        show: false,
                        color: colors.blue,
                    },
                },
                {
                    show: false,
                    seriesName: "Niederschlag",
                    opposite: true,
                    title: {
                        text: "Niederschlag",
                        style: {
                            color: colors.blue,
                        },
                    },
                    labels: {
                        formatter: (val: number) => formatNumber(val, 1),
                        style: {
                            colors: colors.blue,
                        },
                    },
                    axisTicks: {
                        show: false,
                    },
                    axisBorder: {
                        show: false,
                    },
                    min: 0,
                    max: Math.max(5, ...this.data.hourly.map((r) => r.precipitation.amount)),
                },
                {
                    show: false,
                    seriesName: "Niederschlagwarschl.",
                    opposite: true,
                    title: {
                        text: "Niederschlagwarschl.",
                        style: {
                            color: colors.aqua,
                        },
                    },
                    labels: {
                        formatter: (val: number) => formatNumber(val, 0),
                        style: {
                            colors: colors.aqua,
                        },
                    },
                    axisTicks: {
                        show: false,
                    },
                    axisBorder: {
                        show: false,
                    },
                    min: 0,
                    max: 100,
                },
            ],
            tooltip: {
                shared: true,
                x: {
                    formatter: (val: number) => {
                        return categories[val - 1];
                    },
                },
                y: [
                    {
                        formatter: (val: number) => `${formatNumber(val, 1)}°C`,
                    },
                    {
                        formatter: (val: number) => `${formatNumber(val, 1)}mm`,
                    },
                    {
                        formatter: (val: number) => `${formatNumber(val, 0)}%`,
                    },
                ],
            },
            stroke: {
                curve: "monotoneCubic" as const,
                width: [2, 1, 1],
                dashArray: [0, 0, 3],
            },
            annotations: {
                yaxis: [
                    {
                        y: 0,
                        borderColor: colors.black,
                        strokeDashArray: 5,
                        opacity: 0.5,
                    },
                ],
            },
            grid: {
                padding: { left: 0, right: 0, top: 0, bottom: 0 },
                xaxis: { lines: { show: false } },
                yaxis: { lines: { show: false } },
            },
        };
    }

    private _aggregateHourlyData(data: HourlyWeatherPrediction[]) {
        const croppedData = data.slice(2, data.length - 2);
        const aggregatedData: HourlyWeatherPrediction[] = [];
        for (let i = 0; i < croppedData.length; i += 3) {
            const chunk = croppedData.slice(i, i + 3);
            const avgTemperature = chunk.reduce((sum, d) => sum + d.temperature, 0) / chunk.length;
            const totalPrecipitation = chunk.reduce((sum, d) => sum + d.precipitation.amount, 0);
            const maxPrecipitationProbability = Math.max(...chunk.map((d) => d.precipitation.propability));
            const avgWindSpeed = chunk.reduce((sum, d) => sum + d.windSpeed, 0) / chunk.length;
            aggregatedData.push({
                ...chunk[1],
                temperature: avgTemperature,
                precipitation: {
                    amount: totalPrecipitation,
                    propability: maxPrecipitationProbability,
                },
                windSpeed: avgWindSpeed,
            });
        }
        return aggregatedData;
    }

    updated(changes: Map<string, unknown>) {
        if (changes.has("data")) {
            void this._updateChart();
        }
    }

    static styles = [
        shared,
        css`
            :host {
                display: block;
                position: relative;
            }

            img.weather-icon {
                width: 1.5em;
                height: 1.5em;
            }
        `,
    ];

    render() {
        const aggregatedData = this._aggregateHourlyData(this.data.hourly);
        return html`
            <div class="horizontally-padded horizontal center-aligning layout">
                <img
                    class="gianter weather-icon"
                    src="${WEATHER_ICONS_PATH}/${this.data.conditions?.icon || "not-available"}.svg"
                />
                <div class="stretch">
                    <div class="bigger">
                        ${this.city}<span class="smaller subtle"> ${formatDate(this.data.date)}</span>
                    </div>
                    <div class="subtle">${this.data.conditions?.description}</div>
                </div>
                <div class="orange colored-text horizontally-padded horizontally-margined">
                    <div class="huger">${this.data.maxTemperature}°</div>
                    <span class="larger subtle">${this.data.minTemperature}°</span>
                </div>
            </div>
            <div class="vertical layout" style="margin-left: 4.8em">
                <div class="center-aligning horizontal layout">
                    <img class="larger weather-icon" src="${WEATHER_ICONS_PATH}/sunrise.svg" />
                    <div class="bold right-margined">${toTimeString(new Date(this.data.sunrise))}</div>
                    <img class="larger weather-icon" src="${WEATHER_ICONS_PATH}/sunset.svg" />
                    <div class="bold">${toTimeString(new Date(this.data.sunset))}</div>
                </div>
                <div class="center-aligning horizontal layout">
                    <img class="larger weather-icon" src="${WEATHER_ICONS_PATH}/clear-day.svg" />
                    <div>
                        <strong>${Math.floor(this.data.hoursOfSunshine / 3600)}</strong> bis
                        <strong>${Math.ceil(this.data.hoursOfSunshine / 3600)}</strong>
                        Sonnenstunden
                    </div>
                </div>
                <div class="center-aligning horizontal layout">
                    <img class="larger weather-icon" src="${WEATHER_ICONS_PATH}/raindrops.svg" />
                    <div>
                        <strong>${this.data.precipitation.amount}mm</strong>
                        (${this.data.precipitation.propability}%)
                    </div>
                </div>
                <div class="center-aligning horizontal layout">
                    <img class="larger weather-icon" src="${WEATHER_ICONS_PATH}/wind.svg" />
                    <div>bis zu <strong>${this.data.maxWindSpeed} km/h</strong></div>
                </div>
            </div>
            <div class="relative" style="padding: 0 1em 2em 1em;">
                <ptc-chart style="margin: -1em 0 -2.4em 0;"></ptc-chart>
                <div class="horizontal evenly stretching layout">
                    ${aggregatedData.map(
                        (data) => html`
                            <div
                                class="text-centering center-aligning vertical layout"
                                style="transform: translateX(45%); flex: 3;"
                            >
                                <img
                                    class="huger weather-icon"
                                    src="${WEATHER_ICONS_PATH}/${data.conditions?.icon || "not-available"}.svg"
                                />
                                <div class="orange colored-text">
                                    <strong>${Math.floor(data.temperature)}°C</strong>
                                </div>
                                <div class="smaller blue colored-text">
                                    <strong>${formatNumber(data.precipitation.amount, 1)}mm</strong>
                                </div>
                                <div class="smaller aqua colored-text">
                                    ${Math.ceil(data.precipitation.propability)}%
                                </div>
                            </div>
                        `
                    )}
                    <div style="flex: 2"></div>
                </div>
            </div>
        `;
    }
}

@customElement("ptc-weather")
export class Weather extends LitElement {
    @property({ attribute: false })
    data: DailyWeatherPrediction;

    @property()
    city: string;

    @property({ type: Boolean })
    minimal: false;

    static styles = [
        shared,
        css`
            :host {
                display: block;
                position: relative;
            }

            img.weather-icon {
                width: 1.5em;
                height: 1.5em;
            }
        `,
    ];

    render() {
        if (!this.data) {
            return nothing;
        }

        return html`
            <div
                class="horizontal centering layout fullbleed"
                ${popover(html` <ptc-weather-detailed .data=${this.data} .city=${this.city}></ptc-weather-detailed> `, {
                    trigger: "hover",
                    style: "padding: 0;",
                })}
            >
                <img
                    class="bigger weather-icon"
                    src="${WEATHER_ICONS_PATH}/${this.data.conditions?.icon || "not-available"}.svg"
                />
                ${!this.minimal
                    ? html`
                          <div class="orange colored-text left-margined">
                              ${Math.round(this.data.maxTemperature)}°
                              <span class="smaller subtle">${Math.round(this.data.minTemperature)}°</span>
                          </div>
                          <div class="blue colored-text left-margined">${this.data.precipitation.propability}%</div>
                      `
                    : ""}
            </div>
        `;
    }
}
